import type { Tag, TagType } from '../../types/tag.js';
import { TAG_TYPES } from '../../types/tag.js';

/**
 * タグマスタを type ごとにグループ化する。
 * 順序は TAG_TYPES の固定順（技術 → ツール → 開発 → 用途 → セキュリティ → 状態）。
 * 該当タグが 0 件の type も空配列で含む（UI 側で空グループを非表示にするか判断）。
 */
export function groupTagsByType(tags: readonly Tag[]): Record<TagType, Tag[]> {
  const out: Record<TagType, Tag[]> = {
    技術: [],
    ツール: [],
    開発: [],
    用途: [],
    セキュリティ: [],
    状態: [],
  };
  for (const t of tags) {
    if (TAG_TYPES.includes(t.type)) {
      out[t.type].push(t);
    }
  }
  // type 内は name の自然順
  for (const k of TAG_TYPES) {
    out[k].sort((a, b) => a.name.localeCompare(b.name, 'ja'));
  }
  return out;
}

/**
 * 検索クエリでタグマスタを絞り込む。大小文字無視・部分一致。
 * 空文字なら全件返す。trim 済みクエリで判定。
 */
export function filterTagsBySearch(tags: readonly Tag[], query: string): Tag[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...tags];
  return tags.filter((t) => t.name.toLowerCase().includes(q));
}

/**
 * 選択済みタグ名配列を「マスタにあるもの」と「マスタ外（orphan）」に分割する。
 * 編集モードで過去のマスタ外タグが残っているケースを表示するために使う。
 *
 * - master: マスタの Tag オブジェクト（name 一致で引いた結果）
 * - orphan: マスタに存在しないタグ名の配列（順序は引数の順序を保持）
 */
export function splitMasterAndOrphan(
  selected: readonly string[],
  master: readonly Tag[],
): { master: Tag[]; orphan: string[] } {
  const byName = new Map<string, Tag>();
  for (const t of master) byName.set(t.name, t);
  const matched: Tag[] = [];
  const orphan: string[] = [];
  for (const name of selected) {
    const m = byName.get(name);
    if (m) matched.push(m);
    else orphan.push(name);
  }
  return { master: matched, orphan };
}
